'use client';

import { Editor, Element as SlateElement, Point, Range, Transforms } from 'slate';
import { TableElement } from './custom-types';
import { useSlate } from 'slate-react';
import { LucideGrid } from 'lucide-react';
import { TextInput } from '../../form-fields/TextInput';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import clsx from 'clsx';

export const withTables = (editor: Editor) => {
  const { deleteBackward, deleteForward, insertBreak } = editor;

  editor.deleteBackward = (unit) => {
    const { selection } = editor;

    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table-cell',
      });

      if (cell) {
        const [, cellPath] = cell;
        const start = Editor.start(editor, cellPath);

        if (Point.equals(selection.anchor, start)) {
          return;
        }
      }
    }

    deleteBackward(unit);
  };

  editor.deleteForward = (unit) => {
    const { selection } = editor;

    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table-cell',
      });

      if (cell) {
        const [, cellPath] = cell;
        const end = Editor.end(editor, cellPath);

        if (Point.equals(selection.anchor, end)) {
          return;
        }
      }
    }

    deleteForward(unit);
  };

  editor.insertBreak = () => {
    const { selection } = editor;

    if (selection) {
      const [table] = Editor.nodes(editor, {
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table',
      });

      if (table) {
        return;
      }
    }

    insertBreak();
  };

  return editor;
};

function insertTable({ editor, rows, cols }: { editor: Editor; rows: number; cols: number }) {
  const table: TableElement = {
    type: 'table',
    children: Array.from({ length: rows }, () => ({
      type: 'table-row',
      children: Array.from({ length: cols }, () => ({
        type: 'table-cell',
        children: [{ text: '' }],
      })),
    })),
  };

  Transforms.insertNodes(editor, table);
}

export function TableButton() {
  const editor = useSlate();
  const schema = z.object({
    rows: z.number().positive().step(1),
    cols: z.number().positive().step(1),
  });
  const {
    register,
    getValues,
    formState: { isValid },
  } = useForm({
    defaultValues: { rows: 3, cols: 3 },
    resolver: zodResolver(schema),
  });

  const isTableActive = useCallback(() => {
    const [match] = Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table',
    });
    return !!match;
  }, [editor]);

  const deleteTable = useCallback(() => {
    Transforms.removeNodes(editor, {
      match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'table',
      mode: 'highest',
    });
  }, [editor]);

  return (
    <div className="dropdown dropdown-bottom dropdown-end relative">
      <button
        type="button"
        className={clsx('btn btn-xs', isTableActive() && 'btn-primary')}
        onClick={() => {
          if (isTableActive()) deleteTable();
        }}
      >
        <LucideGrid />
        <span className="sr-only">open dropdown to add table</span>
      </button>
      <div className="dropdown-content menu bg-base-200 rounded-box absolute top-0 z-1 grid w-52 grid-cols-2 gap-3 p-3 shadow-2xs">
        <TextInput
          required
          type="number"
          min={0}
          {...register('rows', { valueAsNumber: true })}
          label="Rows"
          className="[&>input]:input-sm"
        />
        <TextInput
          required
          type="number"
          min={0}
          {...register('cols', { valueAsNumber: true })}
          label="Columns"
          className="[&>input]:input-sm"
        />
        <button
          disabled={!isValid}
          type="button"
          className="btn btn-xs btn-primary col-start-2"
          onClick={() => insertTable({ editor, rows: getValues('rows'), cols: getValues('cols') })}
        >
          Add
        </button>
      </div>
    </div>
  );
}
